import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { BRAND } from "../lib/brand";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/book", label: "Book Now" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/90 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="font-serif-display text-2xl sm:text-[26px] font-bold tracking-tight text-ink">
            Six<span className="text-lilac-deep italic">Figure</span>
          </span>
          <span className="hidden sm:inline-block font-serif-display italic text-ink/70 text-lg">
            Couponer
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `relative px-5 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-lilac-deep" : "text-charcoal hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-1 h-[3px] w-6 rounded-full bg-lilac" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/book"
            data-testid="nav-cta-book"
            className="btn-primary inline-flex items-center rounded-full px-6 py-2.5 text-sm font-semibold"
          >
            Book a Session
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2 rounded-lg hover:bg-mint-soft text-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-cream border-t border-border">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-medium ${
                    isActive
                      ? "bg-lilac-soft text-lilac-deep"
                      : "text-charcoal hover:bg-mint-soft"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="nav-mobile-cta-book"
              className="btn-primary mt-2 rounded-full px-6 py-3 text-center text-sm font-semibold"
            >
              Book a Session
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
